import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../utils/passwordPolicy';

const ResetPassword = () => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);
  // Field-validation messages only; the API result goes to a toast.
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Get token from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(PASSWORD_HINT);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        token,
        newPassword
      });

      setResetDone(true);
      toast.success(response.data.message);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);

    } catch (error) {
      toast.error(error.response?.data?.error || 'Password reset failed');
    }

    setLoading(false);
  };

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">SqueakBase</div>
          <p className="auth-subtitle">Invalid Reset Link</p>
          
          <div className="error">
            This password reset link is invalid or has expired.
          </div>

          <div className="reset-info">
            <h4>Link Issues?</h4>
            <p>
              Password reset links expire after 1 hour for security reasons. 
              You may need to request a new reset link.
            </p>
          </div>
          
          <button 
            onClick={() => window.location.href = '/'}
            className="auth-button"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card reset-container">
        <div className="auth-logo">SqueakBase</div>
        <p className="auth-subtitle">
          Create a new password for your account.
        </p>
        
        {!resetDone ? (
          <>
            <div className="reset-info">
              <h4>Reset Your Password</h4>
              <p>
                Please enter your new password below. Make sure it's secure and easy for you to remember.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="error">{error}</div>
              )}

              <div className="form-group">
                <label htmlFor="newPassword">
                  New Password <span className="required">*</span>
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                />
                <div className="field-hint">{PASSWORD_HINT}</div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  Confirm New Password <span className="required">*</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  autoComplete="new-password"
                />
              </div>
              
              <button 
                type="submit" 
                disabled={loading}
                className="auth-button"
              >
                {loading ? (
                  <span className="auth-loading">Resetting Password...</span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="reset-info">
              <h4>Password Reset Successful!</h4>
              <p>
                Your password has been successfully reset. You will be redirected to the sign in page shortly.
              </p>
            </div>
            
            <div className="auth-footer">
              <p className="field-hint">
                Redirecting to sign in in 3 seconds...
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;